// externals
import { useSelector } from 'react-redux';

// libraries
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import { getCreditScoreSource } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { selectors as offerReduxSelectors, type CreditTierByIdDetails } from '@makemydeal/dr-offer-redux';

// components
import { Box } from '@interstate/components/Box';
import {
    InterstateCreditBureauSeparator,
    TwoColumnLayout,
    CreditScoreConfigColumn,
    CreditScoreConfigHeader,
    SoftPullPanel,
    SoftPullScoreHeader,
    SoftPullScoreLabel,
    CreditBureauLinkSection,
    CreditBureauDivider
} from './CreditBureau.styles';
import SelfReportedCreditRatingWithScore from './common/SelfReportedCreditRatingWithScore';
import { Typography } from '@interstate/components/Typography';
import { Badge } from '@interstate/components/Badge';
import { ShieldSolidIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { CreditBureauLink } from './CreditBureauLink';

// utils
import { getCreditScoreSourceLabel } from '../utils/creditScoreSourceLabel';

const distanceToTier = (score: number, tier: CreditTierByIdDetails): number =>
    Math.min(Math.abs(score - tier.score.low), Math.abs(score - tier.score.high));

const mapScoreToTierName = (score: number, tiers: CreditTierByIdDetails[]): string | undefined => {
    const matched = tiers.find((tier) => score >= tier.score.low && score <= tier.score.high);
    if (matched) return matched.name;

    const closest = [...tiers].sort((a, b) => distanceToTier(score, a) - distanceToTier(score, b))[0];
    return closest.name;
};

interface CreditBureauWithScoreProps {
    isLenderFormDirtyRef?: React.MutableRefObject<boolean>;
    showSoftPullPanel?: boolean;
}

const CreditBureauWithScore = ({ isLenderFormDirtyRef, showSoftPullPanel }: CreditBureauWithScoreProps) => {
    const theme = useInterstateTheme();
    const isCreditProviderRouteOne: boolean = useSelector(dealerSelectors.isCreditProviderRouteOne);
    const isSoftPullSource = useSelector(deskingSelectors.getIsSoftPull);
    const softPullCreditScore = useSelector(deskingSelectors.getSoftPullCreditScore);
    const financeCreditTiers = useSelector(offerReduxSelectors.getFinanceCreditTiers);
    const creditScoreSource = useSelector(getCreditScoreSource);

    const isSoftPull = showSoftPullPanel && isSoftPullSource;
    const sourceLabel = getCreditScoreSourceLabel(creditScoreSource);

    const creditScoreLabel =
        softPullCreditScore && financeCreditTiers?.length ? mapScoreToTierName(softPullCreditScore, financeCreditTiers) : undefined;

    return (
        <Box display="flex" flexDirection="column" padding={0} position={'relative'}>
            <InterstateCreditBureauSeparator />
            {!isSoftPull ? (
                <>
                    <Box display="flex" justifyContent="space-between">
                        <Typography tag="span" variant="h5">
                            {sourceLabel} Credit Score
                        </Typography>
                        {!isCreditProviderRouteOne && <CreditBureauLink />}
                    </Box>
                    <br />
                    <SelfReportedCreditRatingWithScore isLenderFormDirtyRef={isLenderFormDirtyRef} />
                </>
            ) : (
                <TwoColumnLayout>
                    <CreditScoreConfigColumn>
                        <CreditScoreConfigHeader>
                            <Typography tag="span" variant="h5">
                                Credit Score Configuration
                            </Typography>
                            <Badge variant="light">{sourceLabel}</Badge>
                        </CreditScoreConfigHeader>
                        <SelfReportedCreditRatingWithScore isLenderFormDirtyRef={isLenderFormDirtyRef} stacked />
                    </CreditScoreConfigColumn>
                    <SoftPullPanel backgroundColor={theme.tokens.SemColorSurfaceSubdued}>
                        <SoftPullScoreHeader>
                            <ShieldSolidIcon color={theme.tokens.SemColorSurfaceButtonPrimaryRest} width="16px" height="16px" />
                            <Typography tag="span" variant="h4" sx={{ fontSize: '18px' }}>
                                Credit Score
                            </Typography>
                            <Badge variant="white">Soft Pull</Badge>
                        </SoftPullScoreHeader>
                        {creditScoreLabel && (
                            <SoftPullScoreLabel>
                                <Typography variant="body-sm" sx={{ color: '#5A5A5A' }}>
                                    {creditScoreLabel}
                                </Typography>
                            </SoftPullScoreLabel>
                        )}
                        {!isCreditProviderRouteOne && (
                            <CreditBureauLinkSection>
                                <CreditBureauDivider />
                                <CreditBureauLink />
                            </CreditBureauLinkSection>
                        )}
                    </SoftPullPanel>
                </TwoColumnLayout>
            )}
        </Box>
    );
};

export default CreditBureauWithScore;
